import React, { useState } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { Button } from './Button';
import { Chip } from './Chip';
import { SelectableCard } from './SelectableCard';
interface RebuildProgramSheetProps {
  onNext: () => void;
  onClose: () => void;
}
export function RebuildProgramSheet({
  onNext,
  onClose
}: RebuildProgramSheetProps) {
  // Preselected defaults (simulating saved user preferences)
  const [goal, setGoal] = useState<string>('build_muscle');
  const [selectedDays, setSelectedDays] = useState<string[]>(['Mon', 'Thu']);
  const [duration, setDuration] = useState<string>('8_weeks');
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const goals = [{
    value: 'build_muscle',
    label: 'Build Muscle'
  }, {
    value: 'lose_weight',
    label: 'Lose Weight'
  }, {
    value: 'get_stronger',
    label: 'Get Stronger'
  }, {
    value: 'recomposition',
    label: 'Recomposition'
  }];
  const durations = [{
    value: '4_weeks',
    label: '4 Weeks'
  }, {
    value: '8_weeks',
    label: '8 Weeks'
  }, {
    value: '12_weeks',
    label: '12 Weeks'
  }];
  const toggleDay = (day: string) => {
    setSelectedDays(prev => prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]);
  };
  const isValid = goal !== '' && selectedDays.length > 0 && duration !== '';
  return <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-end animate-in fade-in duration-200" data-id="element-283">
      <div className="bg-white w-full rounded-t-[2.5rem] p-6 pb-8 shadow-2xl animate-in slide-in-from-bottom-full duration-300 max-h-[90%] flex flex-col" data-id="element-284">
        {/* Header */}
        <div className="flex items-center justify-between mb-2 shrink-0" data-id="element-285">
          <div className="flex items-center gap-3" data-id="element-286">
            <div className="w-10 h-10 bg-blue-50 text-blue-500 rounded-full flex items-center justify-center" data-id="element-287">
              <RefreshCw size={20} data-id="element-288" />
            </div>
            <h2 className="text-xl font-extrabold text-slate-800" data-id="element-289">
              Rebuild your program
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors" data-id="element-290">
            <X size={24} data-id="element-291" />
          </button>
        </div>

        <p className="text-sm font-medium text-slate-500 mb-5 leading-relaxed shrink-0" data-id="element-292">
          Update the details for your next program version. We'll use your
          recent workout data and your current preferences to recommend the best
          structure.
        </p>

        {/* Scrollable Fields */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-6 mb-6" data-id="element-293">
          {/* A. Primary Goal */}
          <div data-id="element-294">
            <label className="text-sm font-bold text-slate-700 ml-1 block mb-1" data-id="element-295">
              Primary goal
            </label>
            <p className="text-xs font-medium text-slate-400 ml-1 mb-3" data-id="element-296">
              Keep your current goal or choose a new one.
            </p>
            <div className="space-y-2" data-id="element-297">
              {goals.map(g => <SelectableCard key={g.value} label={g.label} selected={goal === g.value} onClick={() => setGoal(g.value)} data-id="element-298" />)}
            </div>
          </div>

          {/* B. Training Days */}
          <div data-id="element-299">
            <label className="text-sm font-bold text-slate-700 ml-1 block mb-1" data-id="element-300">
              Which days usually work best for your gym workouts?
            </label>
            <p className="text-xs font-medium text-slate-400 ml-1 mb-3" data-id="element-301">
              Choose the days that usually fit your schedule best.
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-302">
              {weekdays.map(day => <Chip key={day} label={day} selected={selectedDays.includes(day)} onClick={() => toggleDay(day)} data-id="element-303" />)}
            </div>
            {selectedDays.length > 0 && <p className="text-xs font-bold text-emerald-600 ml-1 mt-2 animate-in fade-in" data-id="element-304">
                {selectedDays.length}{' '}
                {selectedDays.length === 1 ? 'day' : 'days'} per week selected
              </p>}
          </div>

          {/* C. Program Duration */}
          <div data-id="element-305">
            <label className="text-sm font-bold text-slate-700 ml-1 block mb-2" data-id="element-306">
              Program duration
            </label>
            <div className="space-y-2" data-id="element-307">
              {durations.map(d => <SelectableCard key={d.value} label={d.label} selected={duration === d.value} onClick={() => setDuration(d.value)} data-id="element-308" />)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="shrink-0" data-id="element-309">
          <Button variant="primary" fullWidth onClick={onNext} disabled={!isValid} data-id="element-310">
            Next
          </Button>
        </div>
      </div>
    </div>;
}